var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/auth.ts
import { EmbeddedAuth } from "@leagueplatform/auth-embedded";
import { StandaloneAuth } from "@leagueplatform/auth-standalone";

// src/util/attach-cookies-with-access-token.util.ts
function exchangeCookie(endpoint, accessToken) {
  return __async(this, null, function* () {
    const request = new Request(endpoint, {
      method: "POST",
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`
      }),
      body: {},
      credentials: "include"
    });
    try {
      yield fetch(request);
    } catch (ignore) {
    }
  });
}
function getCookieExchangeEndpoint(exchangeBase) {
  return `${exchangeBase}/auth0/exchange`;
}
function attachCookiesWithAccessToken(exchangeBase, accessToken) {
  return __async(this, null, function* () {
    const endpoint = getCookieExchangeEndpoint(exchangeBase);
    yield exchangeCookie(endpoint, accessToken);
  });
}

// src/auth.ts
var isStandaloneAuthInitConfig = (config) => !!config.clientOptions;
var notInitializedErrorMessage = "Auth was not initialized";
var Implementation = /* @__PURE__ */ ((Implementation2) => {
  Implementation2["STANDALONE"] = "STANDALONE";
  Implementation2["EMBEDDED"] = "EMBEDDED";
  return Implementation2;
})(Implementation || {});
var _implementation, _contentUrl, _currentToken, _attachContentServerCookie, attachContentServerCookie_fn, _setTokenGetter, setTokenGetter_fn;
var AuthWrapper = class {
  constructor() {
    __privateAdd(this, _attachContentServerCookie);
    // given a token getter, wraps it in common behavior.
    __privateAdd(this, _setTokenGetter);
    __privateAdd(this, _implementation, void 0);
    __privateAdd(this, _contentUrl, "");
    // used to cache the current token so we can avoid unnecessary cookie exchanges.
    __privateAdd(this, _currentToken, "");
  }
  get implementation() {
    if (!__privateGet(this, _implementation)) {
      throw new Error(notInitializedErrorMessage);
    }
    return __privateGet(this, _implementation);
  }
  get initialized() {
    return !!__privateGet(this, _implementation);
  }
  getUserId() {
    return Promise.resolve(void 0);
  }
  getToken() {
    throw new Error(notInitializedErrorMessage);
  }
  /**
   * Initialize this module. Returns a promise which can be awaited, if desired,
   * for confirmation that initialization is complete, but it is not necessary
   * to do so before using any of the other methods of this module.
   */
  initialize(config, contentUrl) {
    return __async(this, null, function* () {
      __privateSet(this, _contentUrl, contentUrl);
      __privateSet(this, _currentToken, "");
      if (isStandaloneAuthInitConfig(config)) {
        __privateSet(this, _implementation, "STANDALONE" /* STANDALONE */);
        StandaloneAuth.initialize(config);
        this.getUserId = () => StandaloneAuth.client.getUser().then((user) => user == null ? void 0 : user["https://el/user_id"]);
        __privateMethod(this, _setTokenGetter, setTokenGetter_fn).call(this, () => StandaloneAuth.client.getTokenSilently());
      } else {
        __privateSet(this, _implementation, "EMBEDDED" /* EMBEDDED */);
        this.getUserId = () => EmbeddedAuth.getLeagueUser().then(({ leagueUserId }) => leagueUserId);
        __privateMethod(this, _setTokenGetter, setTokenGetter_fn).call(this, () => EmbeddedAuth.getToken());
        yield Promise.all([
          EmbeddedAuth.initialize(config),
          EmbeddedAuth.getToken().then(
            (token) => __privateMethod(this, _attachContentServerCookie, attachContentServerCookie_fn).call(this, token)
          )
        ]);
      }
    });
  }
};
_implementation = new WeakMap();
_contentUrl = new WeakMap();
_currentToken = new WeakMap();
_attachContentServerCookie = new WeakSet();
attachContentServerCookie_fn = function(token) {
  return __async(this, null, function* () {
    if (token !== __privateGet(this, _currentToken)) {
      __privateSet(this, _currentToken, token);
      yield attachCookiesWithAccessToken(__privateGet(this, _contentUrl), token);
    }
  });
};
_setTokenGetter = new WeakSet();
setTokenGetter_fn = function(tokenGetter) {
  this.getToken = () => __async(this, null, function* () {
    const token = yield tokenGetter();
    __privateMethod(this, _attachContentServerCookie, attachContentServerCookie_fn).call(this, token);
    return token;
  });
};
var Auth = new AuthWrapper();
export {
  Auth,
  Implementation,
  attachCookiesWithAccessToken
};
//# sourceMappingURL=index.js.map